import { useEffect } from 'react';
import type { LegalAction } from '../api-client.js';
import { Card, RarityGem, type Rarity } from './Card.js';
import { capStat, cardAccent, fallbackRarity, stadiumEffect, type HandCardData } from './HandCard.js';

export type CardPreviewModalProps = {
  card: HandCardData;
  onClose: () => void;
  /**
   * Direct-commit legal actions for this card (contest / tackle / lead /
   * play …). Rendered as labelled buttons. Omit for a pure inspect-only zoom.
   */
  actions?: LegalAction[];
  /**
   * True when this card can be aimed on the field (a disposal: kick / run /
   * handpass / shot). Shows a single "select to aim" button rather than one
   * button per destination — the destinations are tapped on the ground.
   */
  canAim?: boolean;
  busy?: boolean;
  /** Commit one of `actions`. */
  onChoose?: (action: LegalAction) => void;
  /** Select this card for field aiming (disposal). */
  onAim?: () => void;
};

/**
 * Centred modal that shows a card at large size with FULLY LABELLED details,
 * and — when the card can be played right now — the available plays as
 * explicit buttons. Clicking a card always lands here first (it never
 * auto-plays), so a player can read what a card is and does before
 * committing it. Closes on Escape, on backdrop click, or via a button.
 */
export function CardPreviewModal({
  card,
  onClose,
  actions = [],
  canAim = false,
  busy = false,
  onChoose,
  onAim,
}: CardPreviewModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rarity: Rarity = card.rarity ?? fallbackRarity(card.kind);
  const accent = cardAccent(card);
  const canPlay = (actions.length > 0 || canAim) && !!(onChoose || onAim);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ec-ground/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="my-auto flex w-full max-w-sm animate-pop-in flex-col items-center gap-4 motion-reduce:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        <Card size="lg" rarity={rarity} accentColor={accent.color}>
          <PreviewHeader card={card} />
          <PreviewArt card={card} />
          <PreviewBody card={card} />
          <PreviewFooter card={card} rarity={rarity} />
        </Card>

        <div className="w-full rounded-lg border border-ec-line bg-ec-panel/95 p-3 shadow-ec-panel">
          <p className="text-center font-serif text-sm text-ec-bone">{describeCard(card)}</p>

          <CardDetail card={card} />

          {card.flavor && (
            <p className="mt-2 text-center font-serif text-xs italic text-ec-bone-dim">{card.flavor}</p>
          )}

          {/* Plays — explicit, so a card is never committed by a stray tap. */}
          {canPlay && (
            <div className="mt-3 space-y-1.5 border-t border-ec-line pt-3">
              {canAim && onAim && (
                <button
                  type="button"
                  onClick={onAim}
                  disabled={busy}
                  className="block w-full rounded-md border border-ec-brass/60 bg-ec-ground px-3 py-2 text-left font-display text-sm font-medium uppercase tracking-wide text-ec-brass transition hover:border-ec-brass hover:bg-ec-panel-2 disabled:opacity-50"
                >
                  Select to aim — then tap a target on the field →
                </button>
              )}
              {actions.map((action) => (
                <button
                  key={action.descriptorId}
                  type="button"
                  onClick={() => onChoose?.(action)}
                  disabled={busy}
                  className="block w-full rounded-md border border-ec-line bg-ec-ground px-3 py-2 text-left font-display text-sm uppercase tracking-wide text-ec-face transition hover:border-ec-brass hover:bg-ec-panel-2 disabled:opacity-50"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between border-t border-ec-line pt-2">
            <span className="font-serif text-[11px] italic text-ec-bone-dim">
              {canPlay ? 'Pick a play, or close to keep the card.' : 'No legal play right now.'}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-ec-line px-2.5 py-1 font-display text-xs uppercase tracking-wide text-ec-bone transition hover:border-ec-bone hover:text-ec-face"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewHeader({ card }: { card: HandCardData }) {
  // Rarity-foil header (reads `--foil` from the card's rarity class), cream
  // title under a dark wash. Position/tier live in the detail panel below.
  return (
    <div
      style={{
        // Heavy dark wash so the cream title reads on every rarity foil (incl.
        // the light golds) — the bright foil frame keeps the shine.
        background: 'linear-gradient(180deg, rgba(18,15,11,0.62), rgba(18,15,11,0.82)), var(--foil)',
        color: '#f7f1e6',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -2px 0 -1px rgba(19,22,27,0.6)',
      }}
      className="border-b border-ec-ink/40 px-2 py-2"
    >
      <div className="truncate font-display text-[14px] font-semibold uppercase leading-tight tracking-wide [text-shadow:0_1px_2px_rgba(0,0,0,0.85)]">
        {card.name ?? 'Unknown'}
      </div>
    </div>
  );
}

function PreviewArt({ card }: { card: HandCardData }) {
  return (
    <div className="relative flex flex-1 items-center justify-center bg-ec-art shadow-ec-art [background-image:linear-gradient(to_bottom,var(--ec-art-2),var(--ec-art))]">
      <span className="font-display text-[11px] uppercase tracking-wider text-ec-bone-dim">{card.kind}</span>
    </div>
  );
}

function PreviewBody({ card }: { card: HandCardData }) {
  return (
    <div className="border-t border-ec-ink/15 bg-ec-face px-1.5 py-1 text-center font-serif text-[10px] font-medium leading-tight text-ec-ink">
      {bodyTextFor(card)}
    </div>
  );
}

function PreviewFooter({ card, rarity }: { card: HandCardData; rarity: Rarity }) {
  return (
    <div className="flex items-center justify-between border-t border-ec-ink/15 bg-ec-face-2 px-2 py-0.5 font-display text-[9px] uppercase tracking-wider text-ec-ink-soft">
      <span>ECG · S1</span>
      <span className="flex items-center gap-1">
        <span>{card.kind ?? '—'}</span>
        <RarityGem rarity={rarity} />
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fully-labelled detail panel (the readable part — no cryptic abbreviations)
// ---------------------------------------------------------------------------

const STAT_FULL: Record<string, string> = {
  // AFL
  skill: 'Skill',
  power: 'Power',
  speed: 'Speed',
  intelligence: 'Intelligence',
  stamina: 'Stamina',
  // Cricket
  bat: 'Batting',
  bowl: 'Bowling',
  field: 'Fielding',
};

const STAT_ORDER_AFL = ['skill', 'power', 'speed', 'intelligence', 'stamina'];
const STAT_ORDER_CRICKET = ['bat', 'bowl', 'field', 'power', 'speed'];

function pickStatOrder(stats: Record<string, unknown>): string[] {
  if ('bat' in stats || 'bowl' in stats || 'field' in stats) return STAT_ORDER_CRICKET;
  return STAT_ORDER_AFL;
}

/**
 * The clearly-labelled body of the modal: a full-word stat table for
 * players (so "5" is unambiguously "Skill 5"), the position descriptor +
 * tier, and any decision hints the engine attached for the current
 * contest (in/out of position, what a tackle would do).
 */
function CardDetail({ card }: { card: HandCardData }) {
  if (card.kind === 'player') {
    const stats = (card.stats ?? {}) as Record<string, number | undefined>;
    const order = pickStatOrder(stats).filter((k) => typeof stats[k] === 'number');
    const position = card.positionLabel ?? card.position ?? card.role;
    return (
      <div className="mt-3 space-y-2">
        {(position || card.positionTier) && (
          <div className="flex items-center justify-center gap-2 text-xs">
            {position && (
              <span className="font-display font-semibold uppercase tracking-wide text-ec-face">
                {position.replace(/_/g, ' ')}
              </span>
            )}
            {card.positionTier && (
              <span
                className="rounded-sm border border-ec-brass/60 bg-ec-ground px-1.5 py-0.5 font-display text-[10px] font-bold text-ec-brass"
                title="Position tier — tier 1 is the rarest position (biggest in-position bonus), tier 9 the most flexible"
              >
                Tier {String(card.positionTier).replace(/^T/i, '')}
              </span>
            )}
          </div>
        )}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
          {order.map((k) => (
            <div
              key={k}
              className="flex items-center justify-between border-b border-ec-line/70 pb-0.5"
            >
              <dt className="text-xs text-ec-bone-dim">{STAT_FULL[k] ?? k}</dt>
              <dd className="font-display text-sm font-bold tabular-nums text-ec-face">{stats[k]}</dd>
            </div>
          ))}
        </dl>

        <ContestHint card={card} />
      </div>
    );
  }

  // A set/suppress/relief Action or a Conditions card: flag that it OVERRULES
  // (not a +x/−x nudge), then the cost. The override tag is the structural tell.
  if (card.kind === 'condition' || card.spec) {
    const cost = card.cost;
    const isCondition = card.kind === 'condition';
    return (
      <div className="mt-2 space-y-1.5 text-center text-xs text-ec-bone">
        <p className="inline-block rounded-sm border border-ec-brass/60 bg-ec-ground px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-ec-brass">
          {isCondition ? 'Conditions — leaguewide ruling' : 'Override — this contest only'}
        </p>
        {typeof cost === 'number' && cost > 0 && (
          <p className="text-ec-bone-dim">
            Cost: recycle <span className="font-display font-semibold text-ec-brass">{cost}</span> card
            {cost === 1 ? '' : 's'} from your hand to your deck.
          </p>
        )}
      </div>
    );
  }

  // Modifier cards: state the cost in words and the plain-language effect.
  if (card.kind === 'action' || card.kind === 'weather' || card.kind === 'stadium') {
    const cost = card.cost;
    return (
      <div className="mt-2 space-y-1 text-center text-xs text-ec-bone">
        {typeof cost === 'number' && cost > 0 && (
          <p className="text-ec-bone-dim">
            Cost: recycle <span className="font-display font-semibold text-ec-brass">{cost}</span> card
            {cost === 1 ? '' : 's'} from your hand to your deck.
          </p>
        )}
      </div>
    );
  }
  return null;
}

/**
 * Decision hints the engine attaches to a hand card during a contest — the
 * exact thing whose absence causes misplays. Plain language, not numbers.
 */
function ContestHint({ card }: { card: HandCardData }) {
  if (typeof card.contestBonus === 'number') {
    const where = card.contestCellName ? ` at ${card.contestCellName}` : '';
    return card.contestBonus > 0 ? (
      <p className="rounded-md border border-ec-defence bg-[rgba(85,167,47,0.22)] px-2 py-1 text-center text-xs font-medium text-ec-face">
        In position{where} — adds +{card.contestBonus} to this contest.
      </p>
    ) : (
      <p className="rounded-md border border-ec-line bg-ec-ground/60 px-2 py-1 text-center text-xs text-ec-bone-dim">
        Out of position{where} — no position bonus here.
      </p>
    );
  }
  if (card.tackleOutcome) {
    const text =
      card.tackleOutcome === 'ball_won'
        ? 'Wins the ball — turnover.'
        : card.tackleOutcome === 'stoppage'
          ? 'Dead tie — forces a ball-up.'
          : "Can't stop the runner.";
    const tone =
      card.tackleOutcome === 'ball_won'
        ? 'border-ec-defence bg-[rgba(85,167,47,0.22)] text-ec-face'
        : card.tackleOutcome === 'stoppage'
          ? 'border-ec-brass bg-[rgba(245,168,28,0.18)] text-ec-brass-2'
          : 'border-ec-line bg-ec-ground/60 text-ec-bone-dim';
    return (
      <p className={`rounded-md border px-2 py-1 text-center text-xs font-medium ${tone}`}>
        Tackle: {text}
        {card.tackleDetail ? ` (${card.tackleDetail})` : ''}
      </p>
    );
  }
  return null;
}

function fmtBonus(n: number | undefined): string {
  if (typeof n !== 'number' || n === 0) return '';
  return ` ${n > 0 ? '+' : ''}${n}`;
}

const STAT_LABEL_SHORT: Record<string, string> = {
  skill: 'SKL', power: 'PWR', speed: 'SPD', intelligence: 'INT', stamina: 'STA',
  bat: 'BAT', bowl: 'BWL', field: 'FLD',
};

function bodyTextFor(card: HandCardData): string {
  switch (card.kind) {
    case 'player': {
      const stats = card.stats ?? {};
      const order = pickStatOrder(stats as Record<string, unknown>);
      return order
        .filter((k) => typeof (stats as Record<string, unknown>)[k] === 'number')
        .map((k) => `${STAT_LABEL_SHORT[k] ?? k.slice(0, 3).toUpperCase()} ${(stats as Record<string, number>)[k]}`)
        .join(' · ');
    }
    case 'action':
      if (card.subkind === 'shot') {
        return `Shot · BAT${fmtBonus(card.batBonus)}${
          typeof card.risk === 'number' ? ` · risk ${card.risk}` : ''
        }`;
      }
      if (card.subkind === 'delivery') {
        return `Delivery · BOWL${fmtBonus(card.bowlBonus)}${
          typeof card.wicketWeight === 'number' ? ` · wkt ×${card.wicketWeight}` : ''
        }`;
      }
      return card.effect ?? 'Modifier';
    case 'weather':
      if (card.effect) return card.effect; // AFL weather modifies a stat ("Skill −2")
      if (typeof card.batterModifier === 'number') return `Bat${fmtBonus(card.batterModifier)}`;
      return '';
    case 'stadium':
      return stadiumEffect(card);
    case 'time': {
      const skill = -(typeof card.index === 'number' ? card.index : 0);
      return skill !== 0 ? `Skill ${skill} on every shot` : 'Full light — no penalty';
    }
    case 'condition':
      return card.effect ?? 'Leaguewide override';
    default:
      return '';
  }
}

function describeCard(card: HandCardData): string {
  if (card.kind === 'player') {
    const archetype = card.positionLabel ?? card.archetype ?? card.position ?? card.role ?? 'unknown role';
    return `${card.name ?? 'Player'} — ${archetype.replace(/_/g, ' ')}.`;
  }
  if (card.kind === 'action') {
    if (card.subkind === 'shot') {
      return `Shot card. Bat bonus${fmtBonus(card.batBonus) || ' 0'}${
        typeof card.risk === 'number' ? `, risk ${card.risk}` : ''
      }.`;
    }
    if (card.subkind === 'delivery') {
      return `Delivery card. Bowl bonus${fmtBonus(card.bowlBonus) || ' 0'}${
        typeof card.wicketWeight === 'number' ? `, wicket multiplier ×${card.wicketWeight}` : ''
      }.`;
    }
    if (card.spec) {
      // A set/suppress/relief action — overrules ONE contest/action, then spent.
      return `${card.effect ?? 'An override'}. Applies to a single contest or action, then it’s spent (it never persists). It SETS the base (or disregards a position rule); a +x modifier action still stacks on top.`;
    }
    return card.effect ?? 'Action card — a modifier.';
  }
  if (card.kind === 'weather') {
    if (typeof card.batterModifier === 'number') {
      return `Weather card. Batter modifier ${card.batterModifier >= 0 ? '+' : ''}${card.batterModifier}.`;
    }
    const stat = typeof card.modifiesStat === 'string' ? card.modifiesStat : 'skill';
    return `Weather card. ${card.effect ?? `${stat} ${(card.bonus ?? 0) >= 0 ? '+' : ''}${card.bonus ?? 0}`} (folds into ${stat}; indoor venues nullify it).`;
  }
  if (card.kind === 'stadium') {
    const b = typeof card.bonus === 'number' ? card.bonus : 0;
    const passive =
      b !== 0 && card.modifiesStat
        ? `a permanent ground passive — ${capStat(card.modifiesStat)} ${b > 0 ? '+' : ''}${b} to every player, both sides, for the rest of the match`
        : 'no stat change';
    const venue = card.indoor ? 'Indoor: cancels all wind and weather' : 'Outdoor';
    return `Stadium — ${passive}. ${venue}.`;
  }
  if (card.kind === 'time') {
    const skill = -(typeof card.index === 'number' ? card.index : 0);
    const eff = skill !== 0 ? `Skill ${skill} to every shot, both sides` : 'full light — no skill penalty';
    return `Time of day — ${eff}. The hour only ADVANCES (day → twilight → night, never back); nullified by an indoor (lit) stadium.`;
  }
  if (card.kind === 'condition') {
    // A leaguewide ruling: it SETS a base stat or SUPPRESSES position for ALL
    // players, both sides. One universal slot, replaceable (a new one discards
    // the old). NOT a +x/−x modifier — it overrides the printed base.
    return `Conditions — ${card.effect ?? 'a leaguewide override'}, both sides. One universal slot: playing another Conditions card discards this one. It SETS the base (a +x modifier then stacks on top), it does not nudge.`;
  }
  // Set/suppress/relief actions are kind 'action' (handled above via card.spec) —
  // there is no separate 'call' kind any more.
  return card.name ?? 'Card';
}
